/**
 * Queue for preserving order and preventing excessive api calls from a
 * live-search (search as you type) component.
 *
 * https://stackoverflow.com/questions/47238596/reactjs-and-redux-how-to-prevent-excessive-api-calls-from-a-live-search-compon
 *
 * Prevents slower repsonses from getting rendered last in a seach-as-you-type component.
 * Example: the response form request 1 would get rendered last although the request was fired first.
 * 1. 0ms -> makeApiCall() -> 100ms -> assigns response to data
 * 2. 10ms -> makeApiCall() -> 50ms -> assigns response to data
 *
 * Description of Queue
 * 1. If we add a task to the queue, the task goes in front of the queue.
 * 2. If we add a second task to the queue, the task goes in the second position.
 * 3. If we add a third task to the queue, the task replaces the second.
 *
 * There is a maximum of two tasks in the queue. As soon as the first task has ended, the second task is executed etc...
 * So you always have results being rendered in the correct order, and you limit your api calls to two.
 * The first one, for which a response is being awaited;
 * and if present a second one; being always the latest input the user entered into the search field.
 */
class HttpSearchQueue {
  private stack: any[];
  private requesting: boolean;

  constructor() {
    this.requesting = false;
    this.stack = [];
  }

  /**
   *
   * @param queryFunction the query function
   * @param args the arguments for the query function
   */
  async add(queryFunction: any, args: any) {
    if (this.stack.length < 2) {
      return new Promise((resolve, reject) => {
        this.stack.push({
          queryFunction,
          args,
          resolve,
          reject,
        });
        this.makeQuery();
      });
    }
    return new Promise((resolve, reject) => {
      this.stack[1] = {
        queryFunction,
        args,
        resolve,
        reject,
      };
      this.makeQuery();
    }).then((res: any) => {
      return res;
    });
  }

  makeQuery() {
    if (!this.stack.length || this.requesting) {
      return null;
    }
    this.requesting = true;
    this.stack[0].queryFunction
      .apply(this, this.stack[0].args)
      .then((response: any) => {
        this.stack[0].resolve(response);
        this.requesting = false;
        this.stack.splice(0, 1);
        this.makeQuery();
      })
      .catch((error: any) => {
        this.stack[0].reject(error);
        this.requesting = false;
        this.stack.splice(0, 1);
        this.makeQuery();
      });
  }
}
const httpSearchQueue = new HttpSearchQueue();

export default httpSearchQueue;
